import { formatUtcDate } from './format-utc-date';
import { utcDatePrefix } from './utc-date-prefix';

/** Bare `YYYY-MM-DD`, the canonical wire shape of an Aurora `type: date`. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Renders an Aurora `type: date` scalar for display as `DD-MM-YYYY`.
 *
 * **Never converted.** A calendar date has no instant, so the day is read from
 * UTC components — the same frame `au-date-picker` persists in. Shifting it by
 * the viewer's offset would move `2026-08-01` to July 31 for anyone behind UTC.
 * The local-frame counterpart, for values that ARE instants, is
 * `formatIsoDateTime`.
 *
 * A `type: date` lives in a Postgres `date` column, and Drizzle's
 * node-postgres driver already returns it as a raw `YYYY-MM-DD` string. A
 * value that arrives instead as a timestamp with a time component or offset —
 * from a third-party source or an integration outside this contract — still
 * renders correctly here.
 *
 * A value the calendar rejects is echoed rather than rendered as the day
 * `new Date()` rolled it over to: `new Date('2026-02-30')` silently becomes
 * March 2, and showing a day the row never held is worse than showing the raw
 * text. The same applies to a value that does not parse at all.
 *
 * @param iso The ISO scalar, or `null` / `undefined` / `''` when absent.
 * @returns The formatted value, or `''` when the input is absent.
 *
 * @example
 * formatIsoDate('2026-08-01 00:00:00+00'); // → '01-08-2026'
 */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return '';

  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;

  // Round-trip guard: catches a date `new Date()` silently rolled over.
  if (DATE_ONLY.test(iso) && utcDatePrefix(date) !== iso) return iso;

  return formatUtcDate(date);
}
